const LOCK_NAME = 'sciland.auth.refresh';
// Longer than the API client's 12-second deadline. IndexedDB is also available
// on the HTTP test site, where Web Locks (a secure-context API) are unavailable.
const LEASE_MS = 20_000;

function pause(signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const cancel = () => { clearTimeout(timer); reject(signal.reason); };
    const timer = setTimeout(() => { signal.removeEventListener('abort', cancel); resolve(); }, 80);
    signal.addEventListener('abort', cancel, {once: true});
    if (signal.aborted) cancel();
  });
}

function openDatabase(signal: AbortSignal): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('sciland-session-coordination', 1);
    let settled = false;
    const fail = (reason: unknown) => {
      settled = true;
      signal.removeEventListener('abort', cancel);
      reject(reason);
    };
    const cancel = () => fail(signal.reason);
    signal.addEventListener('abort', cancel, {once: true});
    request.onupgradeneeded = () => request.result.createObjectStore('locks');
    request.onerror = () => fail(request.error);
    request.onblocked = () => fail(new Error('Session coordination unavailable'));
    request.onsuccess = () => {
      signal.removeEventListener('abort', cancel);
      if (settled || signal.aborted) request.result.close();
      else { settled = true; resolve(request.result); }
    };
    if (signal.aborted) cancel();
  });
}

function lease(db: IDBDatabase, owner: string, release = false): Promise<number> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction('locks', 'readwrite');
    const store = tx.objectStore('locks');
    const request = store.get(LOCK_NAME);
    let expires = 0;
    request.onsuccess = () => {
      const existing = request.result as {owner: string; expires: number} | undefined;
      if (release) {
        if (existing?.owner === owner) store.delete(LOCK_NAME);
      } else if (!existing || existing.expires <= Date.now()) {
        expires = Date.now() + LEASE_MS;
        store.put({owner, expires}, LOCK_NAME);
      }
    };
    tx.oncomplete = () => resolve(expires);
    tx.onerror = tx.onabort = () => reject(tx.error ?? new Error('Session coordination failed'));
  });
}

export async function withSessionLock<T>(signal: AbortSignal, work: (assertHeld: () => void) => Promise<T>): Promise<T> {
  signal.throwIfAborted();
  if (typeof navigator !== 'undefined' && navigator.locks) {
    return navigator.locks.request(LOCK_NAME, {signal}, () => work(() => signal.throwIfAborted()));
  }
  const db = await openDatabase(signal);
  const owner = crypto.getRandomValues(new Uint32Array(4)).join('-');
  let expires = 0;
  try {
    while (!expires) {
      signal.throwIfAborted();
      expires = await lease(db, owner);
      if (!expires) await pause(signal);
    }
    const assertHeld = () => {
      signal.throwIfAborted();
      if (Date.now() >= expires) throw new DOMException('登录刷新已超时', 'AbortError');
    };
    assertHeld();
    return await work(assertHeld);
  } finally {
    if (expires) await lease(db, owner, true).catch(() => {});
    db.close();
  }
}
